import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Zap, ZapOff, Cog } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Button } from "../ui/button";
import AddGatewayEngineDialog from "./AddGatewayEngineDialog";

const TYPE_LABEL = {
  openai_compatible: "OpenAI compatible",
  gemini: "Gemini",
  anthropic: "Anthropic",
  custom: "API personalizada",
};

export default function GatewayEnginesList({ disabled }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // engine object or "new" or null

  const refresh = async () => {
    try {
      const r = await api.get("/gateway/engines");
      setItems(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (disabled) { setLoading(false); return; }
    refresh();
  }, [disabled]);

  const remove = async (id) => {
    if (!window.confirm(`¿Eliminar el motor '${id}'?`)) return;
    try {
      await api.delete(`/gateway/engines/${id}`);
      toast.success("Motor eliminado");
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "engine_has_routes") {
        const goForce = window.confirm(
          `${d.message}\n\nAliases afectados: ${d.aliases.join(", ")}\n\n¿Eliminar en cascada?`
        );
        if (goForce) {
          try {
            await api.delete(`/gateway/engines/${id}?force=true`);
            toast.success("Motor y rutas eliminados");
            refresh();
          } catch (e2) { toast.error(apiErrorMessage(e2)); }
        }
        return;
      }
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          Motores IA configurados. Solo administración — todavía no se envían peticiones reales.
        </div>
        <Button
          data-testid={TID.gateway.addEngineBtn}
          onClick={() => setEditing("new")}
          disabled={disabled}
          size="sm"
          className="gap-2 bg-white text-black hover:bg-zinc-200"
        >
          <Plus className="h-3.5 w-3.5" /> Añadir motor
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {loading ? (
          <div className="text-xs text-zinc-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="col-span-2 rounded-md border border-dashed border-zinc-800 p-8 text-center text-xs text-zinc-500">
            Aún no has añadido ningún motor.
          </div>
        ) : items.map((e) => (
          <div
            key={e.id}
            data-testid={TID.gateway.engineRow(e.id)}
            className="rounded-md border border-zinc-800 bg-zinc-950 p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Cog className="h-3.5 w-3.5 text-zinc-500" />
                  <span className="truncate">{e.name}</span>
                </div>
                <div className="text-[11px] text-zinc-500 font-mono truncate">
                  {e.id} · {TYPE_LABEL[e.type] || e.type}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  data-testid={TID.gateway.engineEditBtn(e.id)}
                  onClick={() => setEditing(e)}
                  className="p-1.5 text-zinc-400 hover:text-white rounded"
                  aria-label="Editar"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={TID.gateway.engineDeleteBtn(e.id)}
                  onClick={() => remove(e.id)}
                  className="p-1.5 text-zinc-400 hover:text-red-400 rounded"
                  aria-label="Eliminar"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div className="rounded border border-zinc-800/70 bg-black/30 p-2.5 text-[11px] font-mono text-zinc-400 space-y-1">
              <div>URL: <span className="text-zinc-300">{e.base_url}</span></div>
              <div>Key: <span className="text-zinc-300">{e.api_key_masked}</span></div>
              <div>Auth: {e.auth_style}{e.auth_style !== "bearer" ? ` (${e.auth_header_name || e.auth_query_param})` : ""}</div>
              <div>Modelos: {(e.models || []).length}</div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className={
                "inline-flex items-center gap-1.5 text-[11px] " +
                (e.active ? "text-emerald-400" : "text-zinc-500")
              }>
                {e.active ? <Zap className="h-3 w-3" /> : <ZapOff className="h-3 w-3" />}
                {e.active ? "Activo" : "Inactivo"}
              </span>
              <span className="text-[10px] text-zinc-600 font-mono">
                {new Date(e.updated_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <AddGatewayEngineDialog
          engine={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}
